import React from 'react';
import {message} from 'antd';
import type {EventDataNode} from 'antd/es/tree';
import {diagramsApi, foldersApi, projectsApi, type ProjectResponse} from '@/api/projects';
import type {WorkspaceSearchHit} from '@/types/api';
import type {WorkspaceDataNode} from '@/types/workspace/tree.types';
import {
	appendChild,
	diagramKey,
	findNode,
	findParent,
	folderKey,
	mapTree,
	projectKey,
	relocateNode,
	removeNode,
	toChildNode,
	toProjectNode,
	updateTreeData,
} from '@/utils/workspace/treeData';
import {canDeleteProject, canEditContent, canManageProject} from '@/utils/workspace/projectRole';

export function useWorkspaceTree() {
	const [treeData, setTreeData] = React.useState<WorkspaceDataNode[]>([]);
	const [loading, setLoading] = React.useState(true);
	const [loadedKeys, setLoadedKeys] = React.useState<React.Key[]>([]);
	const [expandedKeys, setExpandedKeys] = React.useState<React.Key[]>([]);
	const [selectedKeys, setSelectedKeys] = React.useState<React.Key[]>([]);

	const treeDataRef = React.useRef(treeData);
	treeDataRef.current = treeData;
	const loadedKeysRef = React.useRef(loadedKeys);
	loadedKeysRef.current = loadedKeys;

	const selectedNode = React.useMemo(
		() => (selectedKeys[0] != null ? findNode(treeData, selectedKeys[0]) : undefined),
		[selectedKeys, treeData]
	);

	const loadRoots = React.useCallback(async () => {
		setLoading(true);
		const {data, error} = await projectsApi.list();
		if (error) {
			message.error(error.message || 'Не удалось загрузить проекты');
			setTreeData([]);
		} else {
			setTreeData((data ?? []).map(toProjectNode));
		}
		setLoadedKeys([]);
		setExpandedKeys([]);
		setSelectedKeys([]);
		setLoading(false);
	}, []);

	React.useEffect(() => {
		void loadRoots();
	}, [loadRoots]);

	const loadData = React.useCallback(async (node: EventDataNode<WorkspaceDataNode>) => {
		if (node.nodeType === 'diagram') return;
		const parentFolderId = node.nodeType === 'folder' ? node.entityId : null;
		const {data, error} = await foldersApi.tree(node.projectId, parentFolderId);
		if (error) {
			message.error(error.message || 'Не удалось загрузить содержимое');
			return;
		}
		const children = (data ?? []).map(item => toChildNode(item, node.role));
		setTreeData(origin => updateTreeData(origin, node.key, children));
	}, []);

	const reveal = React.useCallback(async (hit: WorkspaceSearchHit) => {
		const targetKey =
			hit.type === 'PROJECT'
				? projectKey(hit.id)
				: hit.type === 'FOLDER'
					? folderKey(hit.id)
					: diagramKey(hit.id);
		let data = treeDataRef.current;
		const loaded = new Set(loadedKeysRef.current);
		const project = findNode(data, projectKey(hit.projectId));
		if (!project) {
			message.warning('Проект не найден в дереве');
			return;
		}

		const loadChildren = async (node: WorkspaceDataNode) => {
			if (node.nodeType === 'diagram' || loaded.has(node.key)) return;
			const parentFolderId = node.nodeType === 'folder' ? node.entityId : null;
			const {data: children, error} = await foldersApi.tree(node.projectId, parentFolderId);
			if (error) {
				message.error(error.message || 'Не удалось раскрыть дерево');
				return;
			}
			data = updateTreeData(
				data,
				node.key,
				(children ?? []).map(item => toChildNode(item, node.role ?? project.role))
			);
			loaded.add(node.key);
		};

		await loadChildren(project);
		for (const folderId of hit.ancestorFolderIds ?? []) {
			const folder = findNode(data, folderKey(folderId));
			if (!folder) break;
			await loadChildren(folder);
		}

		setTreeData(data);
		setLoadedKeys([...loaded]);
		if (!findNode(data, targetKey)) {
			message.warning('Объект не найден в дереве');
			return;
		}
		const expand = [projectKey(hit.projectId), ...(hit.ancestorFolderIds ?? []).map(folderKey)];
		setExpandedKeys(keys => [...new Set([...keys, ...expand])]);
		setSelectedKeys([targetKey]);
	}, []);

	const addProjectNode = React.useCallback((project: ProjectResponse) => {
		setTreeData(origin => [toProjectNode(project), ...origin]);
		setSelectedKeys([projectKey(project.id)]);
	}, []);

	const renameSelected = React.useCallback((name: string, description?: string | null) => {
		if (!selectedNode) return;
		setTreeData(origin =>
			mapTree(origin, node =>
				node.key === selectedNode.key
					? {...node, title: name, description: description ?? node.description}
					: node
			)
		);
	}, [selectedNode]);

	const addChild = React.useCallback(
		(parent: WorkspaceDataNode, child: WorkspaceDataNode) => {
			if (loadedKeys.includes(parent.key)) {
				setTreeData(origin => appendChild(origin, parent.key, child));
			}
			setExpandedKeys(keys => (keys.includes(parent.key) ? keys : [...keys, parent.key]));
			setSelectedKeys([child.key]);
		},
		[loadedKeys]
	);

	const dropNode = React.useCallback((key: React.Key) => {
		setTreeData(origin => removeNode(origin, key));
		setSelectedKeys(keys => keys.filter(k => k !== key));
		setExpandedKeys(keys => keys.filter(k => k !== key));
		setLoadedKeys(keys => keys.filter(k => k !== key));
	}, []);

	const createFolder = React.useCallback(
		async (name: string) => {
			if (!selectedNode || selectedNode.nodeType === 'diagram' || !canEditContent(selectedNode.role)) return null;
			const parentId = selectedNode.nodeType === 'folder' ? selectedNode.entityId : undefined;
			const {data, error, status} = await foldersApi.create(selectedNode.projectId, {name, parentId});
			if (error || status !== 201 || !data) {
				message.error(error?.message || 'Не удалось создать папку');
				return null;
			}
			message.success('Папка создана');
			addChild(selectedNode, {
				key: folderKey(data.id),
				title: data.name,
				nodeType: 'folder',
				projectId: data.projectId,
				entityId: data.id,
				role: selectedNode.role,
				shared: selectedNode.shared,
				isLeaf: false,
			});
			return data;
		},
		[selectedNode, addChild]
	);

	const createDiagram = React.useCallback(
		async (name: string) => {
			if (!selectedNode || selectedNode.nodeType === 'diagram' || !canEditContent(selectedNode.role)) return null;
			const folderId = selectedNode.nodeType === 'folder' ? selectedNode.entityId : undefined;
			const {data, error, status} = await diagramsApi.create(selectedNode.projectId, {
				name,
				type: 'ARCHIMATE',
				folderId,
			});
			if (error || status !== 201 || !data) {
				message.error(error?.message || 'Не удалось создать диаграмму');
				return null;
			}
			message.success('Диаграмма создана');
			addChild(selectedNode, {
				key: diagramKey(data.id),
				title: data.name,
				nodeType: 'diagram',
				projectId: data.projectId,
				entityId: data.id,
				role: selectedNode.role,
				shared: selectedNode.shared,
				isLeaf: true,
			});
			return data;
		},
		[selectedNode, addChild]
	);

	const importDiagram = React.useCallback(
		async (name: string, content: string) => {
			if (!selectedNode || selectedNode.nodeType === 'diagram' || !canEditContent(selectedNode.role)) return null;
			const folderId = selectedNode.nodeType === 'folder' ? selectedNode.entityId : undefined;
			const {data, error, status} = await diagramsApi.create(selectedNode.projectId, {
				name,
				type: 'ARCHIMATE',
				folderId,
				content,
			});
			if (error || status !== 201 || !data) {
				message.error(error?.message || 'Ошибка импорта');
				return null;
			}
			message.success('Диаграмма импортирована');
			addChild(selectedNode, {
				key: diagramKey(data.id),
				title: data.name,
				nodeType: 'diagram',
				projectId: data.projectId,
				entityId: data.id,
				role: selectedNode.role,
				shared: selectedNode.shared,
				isLeaf: true,
			});
			return data;
		},
		[selectedNode, addChild]
	);

	const moveNode = React.useCallback(
		async (dragNode: WorkspaceDataNode, destParent: WorkspaceDataNode) => {
			if (dragNode.nodeType === 'project' || !canEditContent(dragNode.role) || !canEditContent(destParent.role)) return;
			if (dragNode.projectId !== destParent.projectId) {
				message.warning('Нельзя перенести в другой проект');
				return;
			}
			if (destParent.nodeType === 'diagram') return;
			const parentFolderId = destParent.nodeType === 'folder' ? destParent.entityId : null;
			const currentParent = findParent(treeData, dragNode.key);
			const currentFolderId = currentParent?.nodeType === 'folder' ? currentParent.entityId : null;
			if (currentFolderId === parentFolderId) return;

			const result =
				dragNode.nodeType === 'folder'
					? await foldersApi.move(dragNode.projectId, dragNode.entityId, parentFolderId)
					: await diagramsApi.move(dragNode.projectId, dragNode.entityId, parentFolderId);
			if (result.error) {
				message.error(result.error.message || 'Не удалось переместить');
				return;
			}
			const destLoaded = loadedKeys.includes(destParent.key);
			setTreeData(origin => relocateNode(origin, dragNode.key, destParent.key, destLoaded));
			setExpandedKeys(keys => (keys.includes(destParent.key) ? keys : [...keys, destParent.key]));
		},
		[treeData, loadedKeys]
	);

	const canMutateContent = canEditContent(selectedNode?.role);
	const canAdd = Boolean(canMutateContent && selectedNode && selectedNode.nodeType !== 'diagram');
	const canEdit = Boolean(
		selectedNode &&
			(selectedNode.nodeType === 'project'
				? canManageProject(selectedNode.role)
				: canMutateContent)
	);
	const canDelete = Boolean(
		selectedNode &&
			(selectedNode.nodeType === 'project'
				? canDeleteProject(selectedNode.role)
				: canMutateContent)
	);
	const canShareSelected = Boolean(
		selectedNode && selectedNode.nodeType === 'project' && canManageProject(selectedNode.role)
	);

	return {
		treeData,
		loading,
		loadedKeys,
		setLoadedKeys,
		expandedKeys,
		setExpandedKeys,
		selectedKeys,
		setSelectedKeys,
		selectedNode,
		loadData,
		reveal,
		loadRoots,
		addProjectNode,
		renameSelected,
		dropNode,
		createFolder,
		createDiagram,
		importDiagram,
		moveNode,
		canAdd,
		canEdit,
		canDelete,
		canShareSelected,
	};
}
